import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  auth,
  db,
  storage,
  onAuthStateChanged,
  firebaseSignOut,
} from '../services/firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { startTokenRefresh, stopTokenRefresh } from '../utils/tokenRefresh';
import { signUpWithEmail, signInWithEmail, signInWithGooglePopup, resetPasswordEmail, sendEmailSignInLink, signInAsAnonymous, linkAnonymousToGoogle } from '../services/firebaseAuth';

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const prevUserRef = useRef(null);

  const fetchUserProfile = useCallback(async (uid) => {
    const snap = await getDoc(doc(db, 'users', uid));
    if (snap.exists()) {
      const profile = { id: snap.id, ...snap.data() };
      setUserProfile(profile);
      return profile;
    }
    return null;
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const wasSignedIn = prevUserRef.current !== null;
      prevUserRef.current = user;

      setCurrentUser(user);
      if (user) {
        setSessionExpired(false);
        await fetchUserProfile(user.uid);
        startTokenRefresh();
      } else {
        stopTokenRefresh();
        if (wasSignedIn) {
          setSessionExpired(true);
        } else {
          setUserProfile(null);
        }
      }
      setLoading(false);
    });
    return () => {
      unsubscribe();
      stopTokenRefresh();
    };
  }, [fetchUserProfile]);

  // Auth methods matching DoMoreWorld
  const signup = async (email, password, displayName) => {
    const result = await signUpWithEmail(email, password, displayName);
    if (result.error) {
      throw new Error(result.error);
    }
    return result.user;
  };

  const login = async (email, password) => {
    const result = await signInWithEmail(email, password);
    if (result.error) {
      throw new Error(result.error);
    }
    return result.user;
  };

  const signInWithGoogle = async () => {
    const result = await signInWithGooglePopup();
    if (result.error) {
      throw new Error(result.error);
    }
    return result.user;
  };

  const resetPassword = async (email) => {
    const result = await resetPasswordEmail(email);
    if (result.error) {
      throw new Error(result.error);
    }
  };

  const sendEmailLink = async (email) => {
    const result = await sendEmailSignInLink(email);
    if (result.error) {
      throw new Error(result.error);
    }
  };

  const signInAnonymous = async () => {
    const result = await signInAsAnonymous();
    if (result.error) {
      throw new Error(result.error);
    }
    return result.user;
  };

  const linkAccountToGoogle = async () => {
    const result = await linkAnonymousToGoogle();
    if (result.error) {
      const error = new Error(result.error);
      // Attach error code so UI can handle it specially
      if (result.errorCode) {
        error.code = result.errorCode;
      }
      throw error;
    }
    return result.user;
  };

  const uploadProfilePhoto = async (file) => {
    if (!currentUser) {
      throw new Error('Must be signed in to upload a profile photo');
    }
    const filename = `profile_${currentUser.uid}_${Date.now()}.jpg`;
    const storageRef = ref(storage, `profilePictures/${currentUser.uid}/${filename}`);
    await uploadBytes(storageRef, file, { contentType: file.type || 'image/jpeg' });
    const photoURL = await getDownloadURL(storageRef);

    await updateProfile(currentUser, { photoURL });
    await setDoc(doc(db, 'users', currentUser.uid), { photoURL }, { merge: true });
    await fetchUserProfile(currentUser.uid);
    return photoURL;
  };

  const signOutUser = async () => {
    prevUserRef.current = null;
    setSessionExpired(false);
    setUserProfile(null);
    await firebaseSignOut(auth);
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    sessionExpired,
    signup,
    login,
    signInWithGoogle,
    signInAnonymous,
    linkAccountToGoogle,
    resetPassword,
    sendEmailLink,
    signOutUser,
    uploadProfilePhoto,
    fetchUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
